import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, FileText } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import {
    ESTIMATE_STATUSES,
    StatusBadge,
    money,
    fmtDate,
} from "@/lib/format";
import MobileListCard from "@/components/MobileListCard";
import SwipeableListItem from "@/components/SwipeableListItem";
import { Send } from "lucide-react";

export default function Estimates() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const initialStatus = params.get("status") || "";
    const [list, setList] = useState(null);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(initialStatus);

    const fetchList = async () => {
        setErr("");
        try {
            const { data } = await api.get("/estimates", {
                params: {
                    ...(statusFilter ? { status_filter: statusFilter } : {}),
                    ...(q.trim() ? { q: q.trim() } : {}),
                },
            });
            setList(data.estimates);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        fetchList();
        const next = new URLSearchParams(params);
        if (statusFilter) next.set("status", statusFilter);
        else next.delete("status");
        setParams(next, { replace: true });
        // eslint-disable-next-line
    }, [statusFilter]);

    const counts = useMemo(() => {
        const c = { all: list?.length || 0 };
        ESTIMATE_STATUSES.forEach((s) => (c[s] = 0));
        (list || []).forEach((l) => (c[l.status] = (c[l.status] || 0) + 1));
        return c;
    }, [list]);

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Estimates</p>
                <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
                    <div>
                        <h1 className="h1" data-testid="estimates-title">
                            Estimates
                        </h1>
                        <p className="text-muted mt-2 max-w-[640px]">
                            Quote work, send it to the client, and track every status until it
                            becomes a job.
                        </p>
                    </div>
                    <Link
                        to="/estimates/new"
                        className="btn btn-primary"
                        data-testid="new-estimate-btn"
                    >
                        <Plus size={16} />
                        New estimate
                    </Link>
                </div>
            </header>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className={`btn ${statusFilter === "" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setStatusFilter("")}
                    data-testid="est-filter-all"
                >
                    All ({counts.all})
                </button>
                {ESTIMATE_STATUSES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`btn ${statusFilter === s ? "btn-primary" : "btn-ghost"}`}
                        onClick={() => setStatusFilter(s)}
                        data-testid={`est-filter-${s}`}
                    >
                        <span className="capitalize">{s}</span>
                        <span className="text-xs opacity-70">({counts[s] || 0})</span>
                    </button>
                ))}
            </div>

            <form
                className="mt-4 flex gap-2 flex-wrap"
                onSubmit={(e) => {
                    e.preventDefault();
                    fetchList();
                }}
            >
                <div className="relative flex-1 min-w-[240px]">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]"
                    />
                    <input
                        type="text"
                        placeholder="Search by client name, email, or estimate #"
                        className="field-input"
                        style={{ paddingLeft: "2.25rem" }}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="est-search-input"
                    />
                </div>
                <button type="submit" className="btn btn-secondary" data-testid="est-search-btn">
                    Search
                </button>
            </form>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="est-error"
                >
                    {err}
                </div>
            )}

            <div
                className="brand-card mt-6 p-0 overflow-hidden fade-up"
                data-testid="estimates-card"
            >
                {list === null ? (
                    <div className="p-8 text-muted">Loading estimates…</div>
                ) : list.length === 0 ? (
                    <div className="p-10 text-center">
                        <FileText
                            size={28}
                            style={{ color: "var(--brand-muted)" }}
                            className="mx-auto"
                        />
                        <p className="text-muted mt-3" data-testid="est-empty">
                            No estimates yet. Click <strong>New estimate</strong> to create one.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Mobile cards (under 640px) */}
                        <div className="sm:hidden p-3" data-testid="estimates-cards">
                            {list.map((e) => {
                                const canSend = e.status === "draft";
                                const action = canSend
                                    ? {
                                          label: "Send",
                                          color: "blue",
                                          icon: <Send size={16} />,
                                          onClick: async () => {
                                              try {
                                                  await api.post(`/estimates/${e.id}/send`);
                                                  await fetchList();
                                              } catch (err) {
                                                  setErr(
                                                      apiErrorMessage(err, "Could not send"),
                                                  );
                                              }
                                          },
                                      }
                                    : null;
                                return (
                                    <SwipeableListItem key={e.id} action={action}>
                                        <MobileListCard
                                            to={`/estimates/${e.id}`}
                                            testId={`est-card-${e.id}`}
                                            title={e.estimate_number}
                                            subtitle={e.client?.name}
                                            amount={money(e.grand_total)}
                                            right={<StatusBadge status={e.status} />}
                                            meta={[
                                                { label: "Service", value: e.service_type },
                                                { label: "Date", value: fmtDate(e.created_at) },
                                                { label: "By", value: e.created_by_name },
                                            ]}
                                        />
                                    </SwipeableListItem>
                                );
                            })}
                        </div>
                        {/* Desktop / tablet table (640px+) */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="brand-table" data-testid="estimates-table">
                            <thead>
                                <tr>
                                    <th>Number</th>
                                    <th>Client</th>
                                    <th>Service</th>
                                    <th className="text-right">Amount</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((e) => (
                                    <tr
                                        key={e.id}
                                        onClick={() => navigate(`/estimates/${e.id}`)}
                                        data-testid={`est-row-${e.id}`}
                                    >
                                        <td>
                                            <p className="font-display font-bold">
                                                {e.estimate_number}
                                            </p>
                                            <p className="text-xs text-muted">
                                                by {e.created_by_name || "—"}
                                            </p>
                                        </td>
                                        <td>
                                            <p>{e.client?.name}</p>
                                            <p className="text-xs text-muted">
                                                {e.client?.email || ""}
                                            </p>
                                        </td>
                                        <td className="capitalize text-sm">{e.service_type}</td>
                                        <td className="text-right font-mono">
                                            {money(e.grand_total)}
                                        </td>
                                        <td className="text-sm text-muted">
                                            {fmtDate(e.created_at)}
                                        </td>
                                        <td>
                                            <StatusBadge status={e.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
